import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Facebook,
  Instagram,
  Linkedin,
} from "lucide-react";
import CircuitBackground from "./animations/CircuitBackground";
import Magnetic from "./animations/Magnetic";

export default function Hero() {
  const [headerHeight, setHeaderHeight] = useState(90);
  const [bgImage, setBgImage] = useState("heroisims2.png"); // default desktop image

  // Set header height
  useEffect(() => {
    const header = document.querySelector("header");
    if (header) setHeaderHeight(header.offsetHeight);
  }, []);

  // Update background image based on screen width
  useEffect(() => {
    const updateImage = () => {
      if (window.innerWidth < 768) {
        setBgImage("heroisimsmobile.png"); // mobile
      } else {
        setBgImage("heroisims2.png"); // desktop
      }
    };

    updateImage(); // initial check
    window.addEventListener("resize", updateImage); // update on resize

    return () => window.removeEventListener("resize", updateImage);
  }, []);

  const socialLinks = [
    {
      name: "Facebook",
      icon: Facebook,
      href: "https://www.facebook.com/IEEEStudentBranchISIMSf",
      hoverClass: "hover:bg-[#1877F2] hover:border-[#1877F2] hover:shadow-[0_0_20px_rgba(24,119,242,0.6)]",
    },
    {
      name: "Instagram",
      icon: Instagram,
      href: "https://www.instagram.com/ieee.isims.sb/",
      hoverClass: "hover:bg-gradient-to-tr hover:from-[#f09433] hover:via-[#dc2743] hover:to-[#bc1888] hover:border-[#dc2743] hover:shadow-[0_0_20px_rgba(220,39,67,0.6)]",
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      href: "https://www.linkedin.com/company/ieee-student-branch-isim-sfax/?viewAsMember=true",
      hoverClass: "hover:bg-[#0A66C2] hover:border-[#0A66C2] hover:shadow-[0_0_20px_rgba(10,102,194,0.6)]",
    },
  ];

  return (
    <div className="relative bg-[#f4f7f9] w-full overflow-hidden">
      {/* HERO SECTION */}
      <div
        className="relative"
        style={{ height: `calc(100vh - ${headerHeight}px)` }}
      >
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/${bgImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#006699]/20 to-transparent" />
          {/* Animated Interactive Circuit Background */}
          <CircuitBackground />
        </div>

        {/* TOP LEFT TAGLINE */}
        <motion.div
          initial={{ opacity: 0, x: -30, y: -10 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25, ease: "easeOut" }}
          className="
            absolute z-20
            left-5 sm:left-8 md:left-12 lg:left-16
            top-6 sm:top-8 md:top-12
            pointer-events-none select-none
            max-w-[85vw] md:max-w-none
          "
        >
          <p className="text-white text-base sm:text-xl md:text-3xl lg:text-4xl font-light tracking-[0.08em] sm:tracking-[0.15em] uppercase drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)] break-words md:break-normal md:whitespace-nowrap">
            <span className="text-[#7dd3fc] font-medium">#</span> Great_Stories_<span className="text-[#7dd3fc] font-medium">Begin</span>_Here
          </p>
          <div className="h-[2px] w-16 sm:w-24 bg-[#7dd3fc] mt-2.5 sm:mt-3.5 shadow-[0_0_10px_#7dd3fc]" />
        </motion.div>

        {/* SOCIAL ICONS */}
        <div
          className="
            absolute z-30 flex flex-col gap-3 sm:gap-4
            right-3 sm:right-6 md:right-8
            top-1/2 -translate-y-1/2
          "
        >
          {socialLinks.map((item, i) => {
            const Icon = item.icon;
            return (
              <Magnetic key={item.name} strength={0.4}>
                <motion.a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit IEEE ISIMS SB on ${item.name}`}
                  title={`IEEE ISIMS SB on ${item.name}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
                  whileHover={{ scale: 1.1, x: -4 }}
                  whileTap={{ scale: 0.95 }}
                  className={`
                    cursor-pointer
                    group relative
                    flex items-center justify-center
                    w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12
                    rounded-none
                    bg-black/40 backdrop-blur-md
                    border border-white/30
                    text-white shadow-xl transition-all duration-300
                    ${item.hoverClass}
                  `}
                >
                  {/* Decorative micro L-corners */}
                  <span className="absolute top-0 left-0 w-2 h-2 border-t border-l border-white/60 pointer-events-none group-hover:border-white transition-colors" />
                  <span className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-white/60 pointer-events-none group-hover:border-white transition-colors" />

                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-200 group-hover:scale-110" />
                </motion.a>
              </Magnetic>
            );
          })}
        </div>
      </div>
    </div>
  );
}
